/*
 * Copyright 2023-25 Dell Inc. or its subsidiaries. All Rights Reserved.
 */
import { environmentsDefinitions } from "./environments-definitions";
import { environment } from "../environments/environments";
import { ActionTargetEnum, ActionTypeEnum, UnauthorizedHandleType } from "sirius-platform-support-library/models/common";
import { MicroFrontendTypeEnum } from "sirius-platform-support-library/models/micro-frontends/micro-frontend-type.enum";
import { AuthenticationEvents } from "sirius-platform-support-library/shared/authentication/events/authentication-events";
import { StandardFeatures } from "sirius-platform-support-library/shared/micro-frontends/features/standard-features.constants";
import { StandardActions } from "sirius-platform-support-library/shared/micro-frontends/actions/standard-actions";
import { StandardAuthenticationProviders } from "sirius-platform-support-library/shared/authentication/handlers/standard-authentication.providers";
import { Features } from "./features.constants";
import { ActionEvents, Actions } from "./action.constants";
import { StandardPermissions } from "sirius-platform-support-library/shared/authorization/models/standard-permissions";
import { RequiredPermissionsStructureOperator } from "sirius-platform-support-library/shared/authorization/models/required-permissions-structure-operator";
export function buildTenantContext(localizedResources, routes) {
    var _a, _b;
    return {
        environments: environmentsDefinitions,
        tenantId: '9712a9e7-5b7c-462f-8e76-5b1e32f0a1ce',
        tenantCode: 'tdcc',
        tenantCustomCodeLibrary: {
            url: `${environment.cdnBaseUrl}/tdp-tenant-custom-code${environment.customCodeVersion}/main.js`
        },
        behaviour: {
            pageCustomizations: [{
                    route: '/confirmation/**',
                    customizations: {
                        navbar: {
                            visible: false,
                        },
                        header: {
                            visible: true
                        },
                        footer: {
                            visible: true
                        }
                    }
                }],
            header: {
                enableBreakpointOnTable: true,
                tabletBreakpoint: '1024px'
            },
            microFrontends: {
                loadingErrorPageEnabled: true,
                loadingPlaceholdersEnabled: true
            },
            fullScreenErrorPage: {
                useDefaultLocale: false,
                backgroundColor: '#F6F8FA',
                header: {
                    visible: true,
                },
                footer: {
                    visible: true,
                    titleVisible: true,
                    descriptionVisible: true,
                    copyrightVisible: true,
                    privacyLinkVisible: true,
                    contactLinkVisible: true,
                    aboutLinkVisible: true,
                }
            },
            toasts: {
                location: 'right',
                maximumDisplayedToasts: 5,
                defaultAutoDismiss: 6,
                pauseOnHover: true,
            },
            dialogs: {
                closeDialogOnRouteChange: true
            },
            bannerAlerts: {
                hideNavigationIfNotRequired: true,
            },
            iconsRegistry: {
                loaders: ['CLR', 'CDS', 'UXD', 'DDS'],
                defaultIconSet: 'UXD'
            },
            splashScreen: {
                autoHide: true,
                delayed: true,
                delay: 1000
            },
            featureToggles: {
                defaultNamespace: 'tdp',
                defaultIfNotFound: false
            }
        },
        authentication: {
            enabled: true,
            virtualRoute: {
                enabled: true
            },
            options: {
                progressReporting: {
                    enabled: false
                }
            },
            provider: StandardAuthenticationProviders.DELL_IDENTITY_MANAGED_SESSION_COOKIE,
            providerSettings: {
                connectionId: `${environment.dciClientId}`,
                baseUrl: `${environment.hostUrl}`,
                signInTemplateRoute: `/login`,
                signOutTemplateRoute: `/logout`,
                userInfoUrl: `${environment.usersEndpoint}`,
                sessionRenewalEnabled: true,
                sessionRenewalInterval: 28 * 60 * 1000,
                registrationProcessOptions: {
                    enabled: false,
                }
            }
        },
        analytics: [
            {
                analyticsEngineName: 'GLASSBOX',
                enabled: false,
                userConfigurable: true,
                userPreferenceKey: '',
                requiresPlatformReinitialization: false,
                options: {
                    scriptUrl: environment.glassboxLibraryUrl,
                    applicationName: 'SupportAssist_NextGen'
                }
            },
            {
                analyticsEngineName: 'PENDO',
                enabled: false,
                userConfigurable: true,
                userPreferenceKey: '',
                requiresPlatformReinitialization: false,
                options: {
                    clientKey: '',
                    visitor: {},
                    account: {}
                }
            },
        ],
        localization: {
            supportedLocales: [
                { localeCode: 'zh_CN', label: '简体中文 (CN)' },
                { localeCode: 'zh_TW', label: '繁體中文 (TW)' },
                { localeCode: 'en_US', label: 'English (US)' },
                { localeCode: 'fr_FR', label: 'Français (FR)' },
                { localeCode: 'de_DE', label: 'Deutsch (DE)' },
                { localeCode: 'it_IT', label: 'Italiano (IT)' },
                { localeCode: 'ja_JP', label: '日本語 (JP)' },
                { localeCode: 'ko_KR', label: '한국어 (KR)' },
                { localeCode: 'pt_PT', label: 'Português (PT)' },
                { localeCode: 'ru_RU', label: 'Русский (RU)' },
                { localeCode: 'es_ES', label: 'Español (ES)' },
            ],
            defaultLocaleCode: 'en_US',
            groupByRegionCode: false,
            supportDerivedEnglishLocaleCodes: false,
            refreshOnLocaleChange: false,
            routesWithPageRefreshOnLocaleChange: [],
            loadOnlyCurrentLocaleResources: true,
        },
        site: {
            title: 'TechDirect',
            favIconUrl: '/favicon.ico',
            backgroundColor: '#ffffff',
            fontSize: '125%',
            restrictMaxPageWidth: false,
            tabletBreakpoint: 600,
            top: {
                sticky: true,
                header: {
                    visible: true,
                    backgroundColor: '#ffffff',
                    features: [
                        {
                            code: StandardFeatures.HEADER_LOGO,
                            localizationCode: 'LOGO.ALT_TEXT',
                            enabled: true,
                            options: {
                                imageSrc: `${environment.siriusCdnBaseUrl}/sirius-header-spa-mfe${environment.siriusHeaderVersion}/assets/dell_rectangle_logo.svg`,
                            },
                            actions: []
                        },
                        {
                            code: StandardFeatures.HEADER_TITLE,
                            localizationCode: 'HEADER_TITLE',
                            enabled: true,
                            actions: [
                                {
                                    code: StandardActions.TITLE_LINK,
                                    localizationCode: 'HEADER_TITLE',
                                    type: ActionTypeEnum.URL,
                                    action: '/portal'
                                }
                            ],
                            options: {
                                fontColor: '#0E0E0E'
                            }
                        },
                        {
                            code: StandardFeatures.HEADER_USER_ACTION,
                            enabled: true,
                            index: 2,
                            actions: [
                                {
                                    code: StandardActions.SIGN_IN,
                                    index: 0,
                                    localizationCode: 'USER_ACTIONS.SIGN_IN',
                                    type: ActionTypeEnum.EVENT,
                                    action: AuthenticationEvents.SIGN_IN_EVENT,
                                },
                                {
                                    code: StandardActions.PROFILE,
                                    index: 1,
                                    localizationCode: 'USER_ACTIONS.USER_PROFILE',
                                    type: ActionTypeEnum.URL,
                                    action: '/tdm/user-profile',
                                    data: {
                                        shape: 'cog',
                                        iconSet: 'uxd'
                                    },
                                    authorization: {
                                        permissions: {
                                            operator: RequiredPermissionsStructureOperator.AND,
                                            permissions: [
                                                StandardPermissions.AUTHENTICATED,
                                                'tdp::userStatusActive',
                                                'tdp::validEmailDomain',
                                                'tdp::companyAttested'
                                            ]
                                        },
                                        behaviour: {
                                            unauthorizedHandleType: UnauthorizedHandleType.HIDDEN
                                        }
                                    }
                                },
                                {
                                    code: Actions.HEADER_CHANGE_IMPERSONATION_ACTION,
                                    index: 2,
                                    localizationCode: 'USER_ACTIONS.HEADER_CHANGE_IMPERSONATION_ACTION',
                                    type: ActionTypeEnum.URL,
                                    action: environment.changeImpersonationUrl,
                                    authorization: {
                                        permissions: [
                                            'tdp::isImpersonator'
                                        ],
                                        behaviour: {
                                            unauthorizedHandleType: UnauthorizedHandleType.HIDDEN
                                        }
                                    },
                                    data: {
                                        shape: 'user-search',
                                        iconSet: 'uxd'
                                    }
                                },
                                {
                                    code: StandardActions.SIGN_OUT,
                                    index: 3,
                                    localizationCode: 'USER_ACTIONS.SIGN_OUT',
                                    type: ActionTypeEnum.EVENT,
                                    action: AuthenticationEvents.SIGN_OUT_EVENT,
                                    data: {
                                        shape: 'logout',
                                        iconSet: ''
                                    }
                                },
                            ],
                            options: {
                                minimizedOnTabletBreakpoint: true
                            }
                        },
                        {
                            code: StandardFeatures.LANGUAGE_SELECTOR,
                            index: 1,
                            enabled: true,
                            options: {
                                maxHeight: '360',
                                minimizedOnTabletBreakpoint: true
                            }
                        },
                        {
                            code: StandardFeatures.HEADER_NOTIFICATIONS,
                            index: 0,
                            localizationCode: 'NOTIFICATIONS',
                            enabled: true,
                            authorization: {
                                permissions: {
                                    operator: RequiredPermissionsStructureOperator.AND,
                                    permissions: [
                                        StandardPermissions.AUTHENTICATED,
                                        'tdp::userStatusActive',
                                        'tdp::validEmailDomain',
                                        'tdp::companyAttested'
                                    ]
                                },
                                behaviour: {
                                    unauthorizedHandleType: UnauthorizedHandleType.HIDDEN
                                }
                            },
                            actions: [{
                                    type: ActionTypeEnum.URL,
                                    code: Actions.HEADER_NOTIFICATION_ACTION,
                                    localizationCode: 'NOTIFICATIONS',
                                    action: '/tdm/notifications',
                                }],
                            options: {
                                iconCode: 'bell',
                                minimizedOnTabletBreakpoint: true
                            }
                        },
                    ],
                    items: [
                        {
                            code: Features.HEADER_HELP_CENTER,
                            index: 0,
                            localizationCode: 'MENU_ITEM.HELP_CENTER',
                            iconCode: 'info-circle',
                            action: {
                                code: Actions.HEADER_HELP_CENTER_ACTION,
                                localizationCode: 'MENU_ITEM.HELP_CENTER',
                                type: ActionTypeEnum.URL,
                                action: '/portal/help-center'
                            },
                            options: {
                                minimizedOnTabletBreakpoint: true
                            }
                        },
                        {
                            code: Features.HEADER_CONTACT_US,
                            index: 1,
                            localizationCode: 'MENU_ITEM.CONTACT_US',
                            iconCode: 'envelope',
                            action: {
                                code: Actions.HEADER_CONTACT_US_ACTION,
                                localizationCode: 'MENU_ITEM.CONTACT_US',
                                type: ActionTypeEnum.EVENT,
                                action: ActionEvents.CONTACT_US_ACTION_EVENT
                            },
                            options: {
                                minimizedOnTabletBreakpoint: true
                            }
                        }
                    ],
                    widgets: [],
                    localizedResources: (_a = localizedResources.HEADER) !== null && _a !== void 0 ? _a : {},
                    microFrontend: {
                        name: 'sirius-header-spa-mfe',
                        version: '1.0.0',
                        type: MicroFrontendTypeEnum.SINGLE_SPA,
                        url: `${environment.siriusCdnBaseUrl}/sirius-header-spa-mfe${environment.siriusHeaderVersion}/main.js`,
                    }
                },
                navbar: {
                    visible: true,
                    backgroundColor: '#ffffff',
                    features: [],
                    items: [],
                    localizedResources: {},
                    widgets: [],
                    microFrontend: {
                        name: 'tdp-navbar-mfe',
                        version: '1.0.0',
                        type: MicroFrontendTypeEnum.SINGLE_SPA,
                        url: `${environment.tdpNavBarUrl}`,
                    }
                }
            },
            routing: {
                default: '',
                routes: routes
            },
            footer: {
                visible: true,
                backgroundColor: "#f0f0f0",
                features: [
                    {
                        code: StandardFeatures.FOOTER_TITLE,
                        localizationCode: 'FOOTER_TITLE',
                        enabled: true
                    },
                    {
                        code: StandardFeatures.FOOTER_DESCRIPTION,
                        localizationCode: 'FOOTER_DESCRIPTION',
                        enabled: true
                    },
                    {
                        code: StandardFeatures.FOOTER_COPYRIGHT_LINK,
                        localizationCode: 'PAGE.LINKS.COPYRIGHT',
                        enabled: true,
                        actions: [
                            {
                                code: StandardActions.COPYRIGHT_LINK,
                                localizationCode: 'PAGE.LINKS.COPYRIGHT',
                                type: ActionTypeEnum.URL,
                                action: "https://www.dell.com/learn/us/en/uscorp1/site-terms-of-use-copyright"
                            }
                        ]
                    },
                    {
                        code: StandardFeatures.FOOTER_PRIVACY_LINK,
                        localizationCode: 'PAGE.LINKS.PRIVACY',
                        enabled: true,
                        actions: [
                            {
                                code: StandardActions.PRIVACY_LINK,
                                localizationCode: 'PAGE.LINKS.PRIVACY',
                                type: ActionTypeEnum.URL,
                                action: "https://www.dell.com/learn/us/en/uscorp1/policies-privacy?s=corp",
                                target: ActionTargetEnum.NEW_TAB
                            }
                        ]
                    },
                    {
                        code: StandardFeatures.FOOTER_ABOUT_DELL_LINK,
                        localizationCode: 'PAGE.LINKS.ABOUT',
                        enabled: true,
                        actions: [
                            {
                                code: StandardActions.ABOUT_DELL_LINK,
                                localizationCode: 'PAGE.LINKS.ABOUT',
                                type: ActionTypeEnum.URL,
                                action: "https://www.dell.com/en-us/dt/services/support-services/index.htm"
                            }
                        ]
                    },
                    {
                        code: StandardFeatures.FOOTER_CONTACT_US_LINK,
                        localizationCode: 'PAGE.LINKS.CONTACT_US',
                        enabled: true,
                        actions: [
                            {
                                code: 'FOOTER_CONTACT_US_LINK',
                                localizationCode: 'CONTACT_US',
                                type: ActionTypeEnum.EVENT,
                                action: ActionEvents.CONTACT_US_ACTION_EVENT
                            }
                        ]
                    },
                    {
                        code: StandardFeatures.LANGUAGE_SELECTOR,
                        enabled: true,
                        options: {
                            maxHeight: '360',
                        }
                    }
                ],
                items: [],
                localizedResources: (_b = localizedResources.FOOTER) !== null && _b !== void 0 ? _b : {},
                microFrontend: {
                    name: 'sirius-footer-spa-mfe',
                    version: '1.0.0',
                    type: MicroFrontendTypeEnum.SINGLE_SPA,
                    url: `${environment.siriusCdnBaseUrl}/sirius-footer-spa-mfe${environment.siriusFooterVersion}/main.js`,
                }
            }
        },
    };
}
//# sourceMappingURL=data:application/json;base64,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